import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  Eye,
  Filter,
  PackageCheck,
  RefreshCw,
  Search,
  Truck,
  X,
} from 'lucide-react';
import { irancharsouqAdminApi } from '../../api/irancharsouqAdminApi';
import {
  formatNumber,
  formatToman,
  persianDigits,
  toNumber,
} from '@/features/reports/lib/format';
import { formatJalaliDate, formatOrderNumber } from '@/lib/format';
import JalaliDatePicker from '@/components/JalaliDatePicker';
import {
  getPaymentStatusLabel,
  getVendorAcceptanceLabel,
  getVendorAxisStatusLabel,
} from '@/features/orders/lib/orderStatus';
import {
  OrderMiniProgress,
  OrderStatusBadge,
} from '@/features/orders/components/OrderStatusTimeline';
import ScopedOrderDetailModal from '@/features/orders/components/ScopedOrderDetailModal';
import '@/features/orders/orders.css';
import './adminOrders.css';

const PAGE_SIZE = 20;

const ITEM_STATUSES = [
  ['pending', 'در انتظار بررسی'],
  ['accepted', 'تأیید شده'],
  ['processing', 'در حال آماده‌سازی'],
  ['shipped', 'ارسال شده'],
  ['delivered', 'تحویل داده شده'],
  ['rejected', 'رد شده'],
  ['failed', 'ناموفق'],
  ['cancelled', 'لغو شده'],
];

const PAYMENT_STATUSES = [
  ['unpaid', 'پرداخت نشده'],
  ['prepaid_awaiting_cheque_confirmation', 'در انتظار تأیید چک'],
  ['paid', 'پرداخت شده'],
  ['cancelled_with_refund_due', 'بازپرداخت در انتظار'],
];

const PAYMENT_BADGE_CLASS = {
  unpaid: 'status-pending-badge',
  prepaid_awaiting_cheque_confirmation: 'status-pending-badge',
  paid: 'status-delivered-badge',
  cancelled_with_refund_due: 'status-cancelled-badge',
};

const getAdminSubtotal = (items) => items.reduce(
  (sum, item) => sum + toNumber(item.unit_price) * toNumber(item.quantity),
  0,
);

export default function AdminOrders() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [trackingNumbers, setTrackingNumbers] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const orderTriggerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setState('loading');
    setError('');
    try {
      const nextData = await irancharsouqAdminApi.getOrders({
        page,
        size: PAGE_SIZE,
        search: search || undefined,
        status: statusFilter || undefined,
        payment_status: paymentFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      if (requestId !== requestIdRef.current) return;
      setData(nextData);
      setState('ready');
    } catch (requestError) {
      if (requestId !== requestIdRef.current) return;
      console.error(requestError);
      setError('خطا در دریافت سفارش‌ها. لطفاً دوباره تلاش کنید.');
      setState('error');
    }
  }, [dateFrom, dateTo, page, paymentFilter, search, statusFilter]);

  useEffect(() => {
    let active = true;
    void Promise.resolve().then(() => {
      if (active) return load();
      return undefined;
    });
    return () => {
      active = false;
    };
  }, [load]);

  const runOrderAction = async (orderId, action) => {
    setUpdatingId(orderId);
    setError('');
    try {
      await action();
      await load();
    } catch (requestError) {
      console.error(requestError);
      setError('عملیات سفارش انجام نشد. لطفاً دوباره تلاش کنید.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handover = (orderId) => {
    const trackingNumber = trackingNumbers[orderId]?.trim();
    if (!trackingNumber) {
      setError('کد رهگیری را وارد کنید.');
      return;
    }
    return runOrderAction(orderId, () =>
      irancharsouqAdminApi.handover(orderId, { tracking_number: trackingNumber }),
    );
  };

  const pack = (orderId) =>
    runOrderAction(orderId, () => irancharsouqAdminApi.pack(orderId, {}));

  const closeOrderDetail = useCallback(() => setSelectedOrderId(null), []);

  return (
    <section className="admin-orders-page" dir="rtl">
      <header className="admin-page-header">
        <div>
          <h1 className="admin-page-title">سفارش‌ها</h1>
          <p className="admin-page-subtitle">سفارش‌های شامل محصولات ایران‌چار سوق</p>
        </div>
        <button
          className="admin-refresh-btn"
          disabled={state === 'loading'}
          onClick={() => void load()}
          type="button"
        >
          <RefreshCw aria-hidden="true" size={16} />
          <span>به‌روزرسانی</span>
        </button>
      </header>

      {error && state !== 'error' && <div className="admin-table-error" role="alert">{error}</div>}

      <div className="card admin-orders-toolbar">
        <label className="admin-orders-search">
          <span className="sr-only">جست‌وجو با شماره سفارش</span>
          <Search aria-hidden="true" size={16} />
          <input
            autoComplete="off"
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="جست‌وجو با شماره سفارش"
            type="search"
            value={searchInput}
          />
        </label>
        <label className="admin-orders-filter">
          <Filter aria-hidden="true" size={16} />
          <span className="sr-only">فیلتر وضعیت کالا</span>
          <select
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            value={statusFilter}
          >
            <option value="">همه وضعیت‌های ارسال</option>
            {ITEM_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="admin-orders-filter">
          <span className="sr-only">فیلتر وضعیت پرداخت</span>
          <select
            onChange={(event) => {
              setPaymentFilter(event.target.value);
              setPage(1);
            }}
            value={paymentFilter}
          >
            <option value="">همه وضعیت‌های پرداخت</option>
            {PAYMENT_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="admin-orders-date">
          <span>از تاریخ</span>
          <JalaliDatePicker
            aria-label="از تاریخ"
            max={dateTo || undefined}
            onChange={(value) => {
              setDateFrom(value);
              setPage(1);
            }}
            value={dateFrom}
          />
        </label>
        <label className="admin-orders-date">
          <span>تا تاریخ</span>
          <JalaliDatePicker
            aria-label="تا تاریخ"
            min={dateFrom || undefined}
            onChange={(value) => {
              setDateTo(value);
              setPage(1);
            }}
            value={dateTo}
          />
        </label>
      </div>

      <div className="card admin-table-card">
        <div className="admin-table-wrap">
          <table className="admin-table admin-orders-table">
            <thead>
              <tr>
                <th scope="col">شماره سفارش</th>
                <th scope="col">محصولات</th>
                <th scope="col">خریدار</th>
                <th scope="col">جمع اقلام شما</th>
                <th scope="col">تاریخ</th>
                <th scope="col">پرداخت</th>
                <th scope="col">وضعیت سفارش</th>
                <th scope="col">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {state === 'loading' ? (
                <tr><td className="admin-table-empty" colSpan={8} role="status">در حال دریافت سفارش‌ها...</td></tr>
              ) : state === 'error' ? (
                <tr>
                  <td className="admin-table-empty" colSpan={8}>
                    <span role="alert">{error}</span>
                    <button className="btn-sm btn-sm-success" onClick={() => void load()} type="button">تلاش دوباره</button>
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr><td className="admin-table-empty" colSpan={8}>سفارشی با این مشخصات پیدا نشد.</td></tr>
              ) : data.items.map((order) => {
                const items = order.items ?? [];
                const isCancelled = order.delivery_status === 'cancelled';
                const canRespond = !isCancelled
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'pending',
                  );
                const canPack = !isCancelled
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'accepted'
                      && ['unfulfilled', 'picking', 'packing'].includes(item.vendor_fulfillment_status),
                  );
                const canHandover = !isCancelled
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'accepted'
                      && ['packed', 'ready_for_pickup'].includes(item.vendor_fulfillment_status),
                  );
                const canDeliver = !isCancelled
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'accepted'
                      && item.vendor_fulfillment_status === 'handed_to_carrier',
                  );

                return (
                  <tr key={order.id}>
                    <td>
                      <button
                        aria-label={`نمایش جزئیات سفارش ${formatOrderNumber(order.order_number)}`}
                        className="admin-orders-number"
                        onClick={(event) => {
                          orderTriggerRef.current = event.currentTarget;
                          setSelectedOrderId(order.id);
                        }}
                        type="button"
                      >
                        {formatOrderNumber(order.order_number)}
                      </button>
                    </td>
                    <td className="admin-orders-products">
                      {items.map((item) => (
                        <div key={item.id}>
                          {item.product?.name_fa || 'نام ثبت‌نشده'} × {formatNumber(item.quantity)}
                        </div>
                      ))}
                    </td>
                    <td>
                      <span className="admin-cell-primary">{order.buyer?.name || '—'}</span>
                      <span className="admin-cell-secondary">{persianDigits(order.buyer?.masked_phone || '—')}</span>
                    </td>
                    <td className="admin-cell-primary">{formatToman(getAdminSubtotal(items))}</td>
                    <td className="admin-cell-secondary">
                      {order.created_at ? formatJalaliDate(order.created_at) : '—'}
                    </td>
                    <td>
                      <span className={`status-badge-sm ${PAYMENT_BADGE_CLASS[order.payment_status] || 'badge-gray'}`}>
                        {getPaymentStatusLabel(order.payment_status)}
                      </span>
                    </td>
                    <td>
                      <div className="ost-cell">
                        <OrderStatusBadge order={order} role="seller" />
                        <OrderMiniProgress order={order} role="seller" />
                        {(() => {
                          const states = new Set();
                          for (const item of items) {
                            const acceptance = item.vendor_acceptance_status;
                            const fulfillment = item.vendor_fulfillment_status;
                            if (acceptance && acceptance !== 'accepted') {
                              states.add(getVendorAcceptanceLabel(acceptance));
                            } else if (acceptance || fulfillment) {
                              states.add(getVendorAxisStatusLabel(acceptance, fulfillment));
                            }
                          }
                          if (states.size === 0) return null;
                          return <span className="ost-substatus">{[...states].join(' · ')}</span>;
                        })()}
                      </div>
                    </td>
                    <td>
                      <div className="admin-orders-actions">
                        <button
                          aria-label={`جزئیات سفارش ${formatOrderNumber(order.order_number)}`}
                          onClick={(event) => {
                            orderTriggerRef.current = event.currentTarget;
                            setSelectedOrderId(order.id);
                          }}
                          type="button"
                        ><Eye aria-hidden="true" size={16} /></button>
                        {canRespond && (
                          <>
                            <button
                              aria-label="تأیید کالا"
                              disabled={updatingId === order.id}
                              onClick={() => void runOrderAction(order.id, () => irancharsouqAdminApi.respondToOrder(order.id, 'accepted'))}
                              type="button"
                            ><Check aria-hidden="true" size={16} /></button>
                            <button
                              aria-label="رد کالا"
                              disabled={updatingId === order.id}
                              onClick={() => void runOrderAction(order.id, () => irancharsouqAdminApi.respondToOrder(order.id, 'rejected'))}
                              type="button"
                            ><X aria-hidden="true" size={16} /></button>
                          </>
                        )}
                        {canPack && (
                          <button
                            aria-label="ثبت بسته‌بندی"
                            disabled={updatingId === order.id}
                            onClick={() => void pack(order.id)}
                            type="button"
                          ><PackageCheck aria-hidden="true" size={16} /></button>
                        )}
                        {canHandover && (
                          <>
                            <label className="sr-only" htmlFor={`admin-tracking-${order.id}`}>کد رهگیری سفارش {formatOrderNumber(order.order_number)}</label>
                            <input
                              autoComplete="off"
                              id={`admin-tracking-${order.id}`}
                              maxLength={64}
                              onChange={(event) => setTrackingNumbers((current) => ({
                                ...current,
                                [order.id]: event.target.value,
                              }))}
                              placeholder="کد رهگیری"
                              type="text"
                              value={trackingNumbers[order.id] ?? ''}
                            />
                            <button
                              aria-label="ثبت ارسال"
                              disabled={updatingId === order.id}
                              onClick={() => void handover(order.id)}
                              type="button"
                            ><Truck aria-hidden="true" size={16} /></button>
                          </>
                        )}
                        {canDeliver && (
                          <button
                            aria-label="ثبت تحویل"
                            disabled={updatingId === order.id}
                            onClick={() => void runOrderAction(order.id, () => irancharsouqAdminApi.deliverOrder(order.id))}
                            type="button"
                          ><PackageCheck aria-hidden="true" size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {state === 'ready' && (
          <footer className="admin-pagination admin-orders-pagination">
            <span>مجموع سفارش‌ها: {formatNumber(data.total ?? 0)}</span>
            <div className="admin-pagination-btns">
              <span>صفحه {formatNumber(data.pages ? page : 0)} از {formatNumber(data.pages ?? 0)}</span>
              <button
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                type="button"
              >قبلی</button>
              <button
                disabled={!data.pages || page >= data.pages}
                onClick={() => setPage((current) => current + 1)}
                type="button"
              >بعدی</button>
            </div>
          </footer>
        )}
      </div>

      {selectedOrderId && (
        <ScopedOrderDetailModal
          amountLabel="جمع اقلام شما"
          getAmount={getAdminSubtotal}
          getOrder={irancharsouqAdminApi.getOrder}
          onClose={closeOrderDetail}
          orderId={selectedOrderId}
          triggerRef={orderTriggerRef}
        />
      )}
    </section>
  );
}